import React, { useState } from 'react';
import { 
  Add, DeleteForever 
} from '@mui/icons-material';
import { Fab, Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Selectable, 
  Limitations,
  UpgradableNumber,
  AttachedTable
} from "@dndModels";
import { 
  ABILITY_SCORES, 
  ALL_DRAGONS, 
  DAMAGE_TYPES, 
  DICE_SIZE_OPTIONS, 
  DRAGON_TYPES, 
  SCHOOLS, 
  SELECTABLE_TYPES,
  WEAPON_TYPES
} from "@dndModels/Constants";
import { SelectableType } from "@dndModels/Types";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";

import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import EditableLabel from "@components/input/strings/EditableLabel";
import LimitationsInput from "@dndComponents/limitations/LimitationsInput";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

interface Props {
  obj: Selectable;
  change_time: number;
  disabled: boolean;
  onChange: (changed: Selectable) => void;
  attached_table?: AttachedTable;
}

export default function SelectableInput({ 
  obj,
  change_time,
  disabled,
  onChange,
  attached_table
}: Props) {
  const [ff, setFF] = useState(obj.clone());

  const renderOptions = () => {
    if (ff.type === "Ability Score") {
      return (
        <SelectStringBox 
          name="Ability Scores"
          values={ff.options} 
          options={ABILITY_SCORES}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "Damage Type") {
      return (
        <SelectStringBox 
          name="Damage Types"
          values={ff.options} 
          options={DAMAGE_TYPES}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "School of Magic") {
      return (
        <SelectStringBox 
          name="Schools of Magic"
          values={ff.options} 
          options={SCHOOLS}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "Dragon Type") {
      return (
        <SelectStringBox 
          name="Dragon Types"
          values={ff.options} 
          options={DRAGON_TYPES}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "Dragon Breed") {
      return (
        <SelectStringBox 
          name="Dragon Breeds"
          values={ff.options} 
          options={ALL_DRAGONS}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "Spell List") {
      return (
        <SelectObjectBox 
          name="Spell Lists"
          values={ff.options} 
          data_type="spell_list"
          extra_options={["Any"]}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "Race") {
      return (
        <SelectObjectBox 
          name="Races/Species"
          values={ff.options} 
          data_type="race"
          extra_options={["Any"]}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "Item") {
      // Needs to allow for specific options as well as rules,
      // And it's looking for pseudo_items, not regular items
      ff.limitations.limitation_type = "Item";
      return (
        <LimitationsInput 
          obj={ff.limitations} 
          disabled={disabled} 
          change_time={change_time} 
          onChange={(changed: Limitations) => {
            const new_obj = obj;
            new_obj.limitations = changed.clone();
            onChange(new_obj);
          }}
        />
      );
    } else if (ff.type === "Weapon Type") {
      return (
        <SelectStringBox 
          name="Weapon Types"
          values={ff.options} 
          options={WEAPON_TYPES}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    } else if (ff.type === "Spell" || ff.type === "Character Spell") {
      // Needs to allow for specific options as well as rules,
      // And it's looking for pseudo_items, not regular items
      ff.limitations.limitation_type = "Spell";
      return (
        <LimitationsInput 
          obj={ff.limitations} 
          disabled={disabled} 
          change_time={change_time} 
          onChange={(changed: Limitations) => {
            const new_obj = obj;
            new_obj.limitations = changed.clone();
            onChange(new_obj);
          }}
        />
      );
    } else if (ff.type === "Strings") {
      return renderStrings();
    } else if (ff.type === "Number") {
      return (<span>The Character will be able to enter a number.</span>);
    } else if (ff.type === "Dice Size") {
      return (
        <SelectStringBox 
          name="Dice Sizes"
          values={ff.options} 
          options={DICE_SIZE_OPTIONS}
          disabled={disabled}
          multiple
          onChange={(changed: string[]) => {
            const new_obj = ff;
            new_obj.options = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      );
    }
    return (<span>{ ff.type } not implemented yet</span>);
  }

  const renderStrings = () => {
    return (
      <Grid container spacing={0} direction="column">
        <Grid>
          <Tooltip title={`Add String`}>
            <span>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                disabled={disabled}
                onClick={ () => {
                  const new_obj = ff;
                  new_obj.options.push("");
                  setFF(new_obj);
                  onChange(new_obj);
                }}>
                <Add/>
              </Fab>
            </span>
          </Tooltip>
        </Grid>
        { ff.options.map((opt, key) => {
          return (
            <Grid key={key} container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 10
                }}>
                <EditableLabel 
                  name="String"
                  value={opt}
                  disabled={disabled}
                  onChange={(changed: string) => {
                    const new_obj = ff;
                    new_obj.options[key] = changed;
                    setFF(new_obj);
                    onChange(new_obj);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 2
                }}>
                <Tooltip title={`Remove String`}>
                  <span>
                    <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                      disabled={disabled}
                      onClick={ () => {
                        const new_obj = ff;
                        new_obj.options.splice(key, 1);
                        setFF(new_obj);
                        onChange(new_obj);
                      }}>
                      <DeleteForever/>
                    </Fab>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <StringBox 
          name="Key"
          value={ff.the_key} 
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = ff;
            new_obj.the_key = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 9
        }}>
        <SelectStringBox 
          name="Type"
          value={ff.type} 
          options={SELECTABLE_TYPES}
          disabled={disabled}
          onChange={(changed: SelectableType) => {
            const new_obj = ff;
            new_obj.type = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        <UpgradableNumberBox 
          name="Count"
          value={ff.count} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const new_obj = ff;
            new_obj.count = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        { renderOptions() }
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <CheckBox 
          name="Show in Source Name"
          value={ff.show_in_source_name} 
          disabled={disabled}
          onChange={(changed: boolean) => {
            const new_obj = ff;
            new_obj.show_in_source_name = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <CheckBox 
          name="Feature Source Specific"
          value={ff.fs_specific} 
          disabled={disabled}
          onChange={(changed: boolean) => {
            const new_obj = ff;
            new_obj.fs_specific = changed;
            setFF(new_obj);
            onChange(new_obj);
          }} 
        />
      </Grid>
    </Grid>
  );
}
